import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Plus, Search, Filter, MoreVertical, ShieldCheck, Mail, Phone, Eye, Trash2, Edit, Clock } from 'lucide-react';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import Drawer from '../../components/common/Drawer/Drawer';
import api from '../../api/axios';
import { confirmAction } from '../../utils/confirmToast';
import styles from './SecurityStaff.module.css';

const SecurityStaff = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingStaff, setEditingStaff] = useState(null);

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    shift: 'Morning'
  });

  useEffect(() => {
    fetchStaff();
  }, []);

  const fetchStaff = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/admin/security-staff');
      setStaff(data);
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
      toast.error('Failed to load security staff');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const openAddDrawer = () => {
    setEditingStaff(null);
    setFormData({ fullName: '', email: '', phone: '', password: '', shift: 'Morning' });
    setIsDrawerOpen(true);
  };

  const openEditDrawer = (s) => {
    setEditingStaff(s);
    setFormData({ fullName: s.fullName, email: s.email, phone: s.phone || '', password: '', shift: s.shift || 'Morning' });
    setIsDrawerOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingStaff) {
        const updateData = { fullName: formData.fullName, email: formData.email, phone: formData.phone };
        if (formData.password) updateData.password = formData.password;
        if (formData.shift) updateData.shift = formData.shift;
        
        const { data } = await api.put(`/admin/security-staff/${editingStaff._id}`, updateData);
        setStaff(staff.map(s => s._id === editingStaff._id ? data : s));
        toast.success('Security staff updated successfully!');
      } else {
        const { data } = await api.post('/admin/security-staff', formData);
        setStaff([data, ...staff]);
        toast.success('Security staff added successfully!');
      }
      setIsDrawerOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Action failed');
    }
  };

  const handleDelete = async (id) => {
    confirmAction("Are you sure you want to remove this security guard?", async () => {
      try {
        await api.delete(`/admin/security-staff/${id}`);
        setStaff(staff.filter(s => s._id !== id));
        toast.success('Security staff removed successfully!');
      } catch (error) {
        toast.error('Failed to delete staff');
      }
    });
  };

  return (
    <div className={styles.container}>
      <PageHeader 
        title="Security Staff Management" 
        breadcrumb={['Society Management', 'Security Staff']} 
        actionButton={
          <button className={styles.addBtn} onClick={openAddDrawer}>
            <Plus size={16} /> Add New Guard
          </button>
        }
      />

      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div className={styles.searchBox}>
            <Search size={16} className={styles.searchIcon} />
            <input type="text" placeholder="Search by name or email..." />
          </div>
        </div>

        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Guard Name</th>
                <th>Contact Info</th>
                <th>Role</th>
                <th>Shift</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan="5">Loading...</td></tr> : staff.map(s => (
                <tr key={s._id}>
                  <td>
                    <div className={styles.residentName}>
                      {s.profileImage && s.profileImage !== 'https://cdn-icons-png.flaticon.com/512/3135/3135715.png' ? (
                        <img 
                          src={s.profileImage.startsWith('http') ? s.profileImage : `${import.meta.env.VITE_API_URL.replace(/\/api\/?$/, '')}${s.profileImage.startsWith('/') ? '' : '/'}${s.profileImage}`} 
                          alt={s.fullName} 
                          className={styles.avatarSmall} 
                          style={{ objectFit: 'cover' }} 
                        />
                      ) : (
                        <div className={styles.avatarSmall}>{s.fullName.charAt(0).toUpperCase()}</div>
                      )}
                      <strong>{s.fullName}</strong>
                    </div>
                  </td>
                  <td>
                    <div className={styles.contactInfo}>
                      <span><Phone size={12} /> {s.phone || '-'}</span>
                      <span><Mail size={12} /> {s.email}</span>
                    </div>
                  </td>
                  <td><span className={styles.badgeType}>Security</span></td>
                  <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#f1f5f9', padding: '4px 8px', borderRadius: '4px', fontSize: '13px' }}>
                      <Clock size={12} /> {s.shift || 'Morning'}
                    </span>
                  </td>
                  <td>
                    <span className={`${styles.badgeStatus} ${styles.active}`}>
                      <ShieldCheck size={12} /> Active
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button className={styles.actionBtn} onClick={() => openEditDrawer(s)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#3b82f6' }} title="Edit Guard">
                        <Edit size={16} />
                      </button>
                      <button className={styles.actionBtn} onClick={() => handleDelete(s._id)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#ef4444' }} title="Remove Guard">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className={styles.mobileList}>
            {loading ? <div style={{padding: '20px', textAlign: 'center'}}>Loading...</div> : staff.map(s => (
              <div key={s._id} className={styles.mobileCard}>
                <div className={styles.cardTop}>
                  <div className={styles.residentName}>
                    {s.profileImage && s.profileImage !== 'https://cdn-icons-png.flaticon.com/512/3135/3135715.png' ? (
                      <img 
                        src={s.profileImage.startsWith('http') ? s.profileImage : `${import.meta.env.VITE_API_URL.replace(/\/api\/?$/, '')}${s.profileImage.startsWith('/') ? '' : '/'}${s.profileImage}`} 
                        alt={s.fullName} 
                        className={styles.avatarSmall} 
                        style={{ objectFit: 'cover' }} 
                      />
                    ) : (
                      <div className={styles.avatarSmall}>{s.fullName.charAt(0).toUpperCase()}</div>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                      <strong>{s.fullName}</strong>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <span className={styles.badgeType}>Security</span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px', fontSize: '11px', color: '#475569' }}>
                          <Clock size={10} /> {s.shift || 'Morning'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <span className={`${styles.badgeStatus} ${styles.active}`}>
                    <ShieldCheck size={12} /> Active
                  </span>
                </div>
                <div className={styles.cardBottom}>
                  <div className={styles.contactInfo}>
                    <span><Phone size={12} /> {s.phone || '-'}</span>
                    <span><Mail size={12} /> {s.email}</span>
                  </div>
                  <div className={styles.cardActions}>
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <button className={styles.actionBtn} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#3b82f6', display: 'flex', padding: '6px' }} onClick={() => openEditDrawer(s)} title="Edit Guard">
                        <Edit size={16} />
                      </button>
                      <button className={styles.actionBtn} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#ef4444', display: 'flex', padding: '6px' }} onClick={() => handleDelete(s._id)} title="Remove Guard">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Drawer 
        isOpen={isDrawerOpen} 
        onClose={() => setIsDrawerOpen(false)} 
        title={editingStaff ? "Edit Security Guard" : "Add New Security Guard"}
      >
        <form className="drawerForm" onSubmit={handleSubmit}>
          <div className="formGroup">
            <label>Full Name</label>
            <input name="fullName" value={formData.fullName} onChange={handleChange} required placeholder="Guard full name" />
          </div>
          <div className="formGroup">
            <label>Phone Number</label>
            <input name="phone" value={formData.phone} onChange={handleChange} required placeholder="Contact number" />
          </div>
          <div className="formGroup">
            <label>Email Address</label>
            <input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="Email (used for login)" />
          </div>
          <div className="formGroup">
            <label>Password {editingStaff && "(Leave blank to keep current)"}</label>
            <input type="password" name="password" value={formData.password} onChange={handleChange} required={!editingStaff} placeholder={editingStaff ? "New password" : "Password"} />
          </div>
          <div className="formGroup">
            <label>Shift Assignment</label>
            <select name="shift" value={formData.shift} onChange={handleChange} required style={{ width: '100%', padding: '10px 14px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '0.95rem' }}>
              <option value="Morning">Morning</option>
              <option value="Evening">Evening</option>
              <option value="Night">Night</option>
              <option value="Reliever">Reliever</option>
              <option value="Off">Off</option>
            </select>
          </div>
          
          <div className="formActions">
            <button type="button" className="btnCancel" onClick={() => setIsDrawerOpen(false)}>Cancel</button>
            <button type="submit" className="btnSubmit">Save Guard</button>
          </div>
        </form>
      </Drawer>
    </div>
  );
};

export default SecurityStaff;
